import React, { useMemo } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import { StackedColumnCategoryChart, StackedColumnsCategoryChartProps } from '../../../../components/chart/stackedColumnsCategoryChart';
import { ISmartBatteryFirstSeenIntervalStats } from '../../../../services/shared/battery/batteryCentricService';
import Card from '@components/card/card';
import '../style.css';
import { createChartTooltipAdapter } from './lib';

interface IProps {
  data: ISmartBatteryFirstSeenIntervalStats,
  isLoading?: boolean
}

interface BatteryFirstSeenChartData {
  bucket: string,
  count: number
}

const series = [
  {
    visible: true,
    dataKey: 'count' as keyof BatteryFirstSeenChartData,
    description: 'Batteries First Seen Count',
    colour: '#5b9ad2'
  },
];

export function BatteryFirstSeenChartContainer(props: IProps) {
  const ns = 'batteryEssentialsChart';
  const { t } = useTranslation(ns);

  const chartData: BatteryFirstSeenChartData[] = useMemo(() => {
    if (!props.data) return [];

    const data = [
      {
        bucket: '0-1',
        count: props.data.months0To1,
      },
      {
        bucket: '1-2',
        count: props.data.months1To2,
      },
      {
        bucket: '2-3',
        count: props.data.months2To3,
      },
      {
        bucket: '3-6',
        count: props.data.months3To6,
      },
      {
        bucket: '6-12',
        count: props.data.months6To12,
      },
      {
        bucket: '>12',
        count: props.data.over12Months,
      }
    ];
    return data;
  }, [props.data]);

  const chartProps: StackedColumnsCategoryChartProps<BatteryFirstSeenChartData> = {
    data: !props.isLoading ? chartData : null,
    series,
    title: t('BATTERY_FIRST_SEEN_CHART_TITLE'),
    cypressId: 'battery-first-seen-chart',
    className: 'battery-first-seen-chart',
    hasLegend: false,
    bottomBorder: false,
    columnWidth: 50,
    yAxisType: 'value',
    useAxisTooltip: true,
    amchartsYAxisNumberFormat: '#.',
    categoryKey: 'bucket',
    drawXAxis: (chart, xAxis) => {
      xAxis.title.text = t('BATTERY_FIRST_SEEN_CHART_LABEL');
      xAxis.renderer.labels.template.fontSize = 12;
      xAxis.renderer.labels.template.horizontalCenter = 'middle';
      xAxis.renderer.labels.template.verticalCenter = 'middle';
      xAxis.renderer.minGridDistance = 20;
      return xAxis;
    },
    drawYAxis: (chart, yAxis) => {
      yAxis.title.text = t('NO_OF_BATTERIES');
      yAxis.title.fontSize = 12;
      yAxis.renderer.labels.template.fontSize = 12;
      yAxis.min = 0;
      yAxis.strictMinMax = false;
      yAxis.renderer.minGridDistance = 30;
      yAxis.maxPrecision = 0;
      return yAxis;
    },
    getAxisTooltipAdapter: () => createChartTooltipAdapter(t)
  };

  return (
    <Card noPadding={false} className="battery-first-seen-container">
      <StackedColumnCategoryChart<any>
        {...chartProps}
      />
    </Card>
  );
}
